import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HeroSlide } from '@/types/content';

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const go = (n: number) => setIndex((n + slides.length) % slides.length);
  const current = slides[index];

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-foreground-950 md:h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点推荐"
    >
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            title={`${s.title} - 靠逼`}
            className="h-full w-full object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/25" />

      <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-12 md:px-8 md:pb-16">
        <Link
          to={`/detail/${current.linkId}`}
          className="w-full max-w-2xl animate-fadeUp"
          key={current.id}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-fill" />
            {current.tag}
          </span>
          <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-white md:text-4xl">
            {current.title}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">{current.excerpt}</p>
          <span className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            阅读全文
            <i className="ri-arrow-right-line" />
          </span>
        </Link>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-white transition-colors hover:bg-foreground-950/70 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-white transition-colors hover:bg-foreground-950/70 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 right-4 flex items-center gap-2 md:right-8 md:bottom-8">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}